import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Paper,
  TextField,
  IconButton,
  Collapse,
  List,
  ListItem,
  ListItemText,
  Tooltip,
  CircularProgress,
  Chip,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import FlagIcon from "@mui/icons-material/Flag";
import FlagOutlinedIcon from "@mui/icons-material/FlagOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import HistoryIcon from "@mui/icons-material/History";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import {
  getVendorReviewSubcategories,
  getVendorReviewQuestionnaire,
  flagVendorAnswer,
  submitVendorReview,
} from "./vendorApiService";
import SuccessModal from "./SuccessModal";
import ReusableButton from "../../UpdatedComponents/ReusableButton";
import { useSnackbar } from "../../ReusableComponents/SnackbarManager.js";
import "../compliance/PolicyDoc.css";

const PRIMARY = "#001b2b";
const SECTION_HEADER_BG = "#e0f7fa";
const FLAG_COLOR = "#f44336";
const FLAG_BG = "#fff3e0";

const CHOICE_LABELS = {
  YES: "Yes",
  NO: "No",
  PARTIAL: "Partial",
  NOT_APPLICABLE: "Not Applicable",
};

const CHOICE_COLORS = {
  YES: "#4caf50",
  NO: "#f44336",
  PARTIAL: "#ff9800",
  NOT_APPLICABLE: "#9e9e9e",
};

const REVIEW_INSTRUCTIONS = [
  {
    step: 1,
    title: "Review the Questionnaire",
    text: "Go through each section and review the answers, comments, and evidence provided on your behalf.",
  },
  {
    step: 2,
    title: "Flag Incorrect Answers",
    text: "If any answer, comment, or evidence is incorrect or needs correction, click the flag icon next to that question and optionally provide your feedback.",
  },
  {
    step: 3,
    title: "Submit Your Review",
    text: 'Once you\'ve reviewed all questions, click "Submit Review". If you flagged any items, they will be sent back for corrections. If everything is correct, the assessment will be acknowledged.',
  },
];

const VendorReviewQuestionnaire = ({ reviewToken, vendorName, onSubmitSuccess }) => {
  const [currentTab, setCurrentTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [questionnaire, setQuestionnaire] = useState(null);
  const [expandedSections, setExpandedSections] = useState({});
  const [flags, setFlags] = useState({}); // { answerId: { is_flagged, flag_comment } }
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [flagComments, setFlagComments] = useState({}); // { answerId: string }
  const [savingFlag, setSavingFlag] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);
  const showSnackbar = useSnackbar();

  useEffect(() => {
    loadQuestionnaire();
  }, [reviewToken]);

  const loadQuestionnaire = async () => {
    setLoading(true);
    try {
      const params = { review_token: reviewToken };

      // Step 1: Fetch subcategories
      const subcategories = await getVendorReviewSubcategories(params);

      if (!subcategories || !Array.isArray(subcategories) || subcategories.length === 0) {
        showSnackbar("No subcategories found for this assessment", "error");
        setLoading(false);
        return;
      }

      // Step 2: For each subcategory, fetch sections/questions
      const subcatDataPromises = subcategories.map(async (subcat) => {
        const qParams = {
          review_token: reviewToken,
          subcategory_id: subcat.id,
          subcategory_type: subcat.type,
        };
        const sections = await getVendorReviewQuestionnaire(qParams);
        return {
          subcategory: subcat,
          sections: Array.isArray(sections) ? sections : [],
        };
      });

      const subcatResults = await Promise.all(subcatDataPromises);

      // Step 3: Build structure
      const builtData = {
        categories: [
          {
            category_name: "Vendor Risk Assessment Review",
            subcategories: subcatResults.map((result) => ({
              subcategory_name: result.subcategory.name,
              subcategory_id: result.subcategory.id,
              subcategory_type: result.subcategory.type,
              sections: result.sections.map((section) => ({
                section_id: section.section_id,
                section_name: section.section_name,
                section_type: section.section_type,
                weightage: section.weightage,
                questions: section.questions || [],
              })),
            })),
          },
        ],
      };

      setQuestionnaire(builtData);

      // Initialize flags and flag comments from existing data
      const initialFlags = {};
      const initialFlagComments = {};
      builtData.categories.forEach((category) => {
        category.subcategories?.forEach((subcategory) => {
          subcategory.sections?.forEach((section) => {
            section.questions?.forEach((question) => {
              const answerId = question.vendor_risk_assessment_answer_id;
              if (question.is_flagged) {
                initialFlags[answerId] = {
                  is_flagged: true,
                  flag_comment: question.flag_comment || "",
                };
                initialFlagComments[answerId] = question.flag_comment || "";
              }
            });
          });
        });
      });
      setFlags(initialFlags);
      setFlagComments(initialFlagComments);
    } catch (error) {
      showSnackbar(error.message || "Failed to load questionnaire for review", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  const handleSectionToggle = (sectionId) => {
    setExpandedSections((prev) => ({
      ...prev,
      [sectionId]: !prev[sectionId],
    }));
  };

  const handleToggleFlag = async (answerId) => {
    const isCurrentlyFlagged = flags[answerId]?.is_flagged;

    if (isCurrentlyFlagged) {
      // Unflag
      setSavingFlag((prev) => ({ ...prev, [answerId]: true }));
      try {
        await flagVendorAnswer({
          review_token: reviewToken,
          vendor_risk_assessment_answer_id: answerId,
          is_flagged: false,
          flag_comment: "",
        });

        setFlags((prev) => {
          const newFlags = { ...prev };
          delete newFlags[answerId];
          return newFlags;
        });
        setFlagComments((prev) => ({ ...prev, [answerId]: "" }));
        showSnackbar("Flag removed", "info");
      } catch (error) {
        showSnackbar("Failed to remove flag", "error");
      } finally {
        setSavingFlag((prev) => ({ ...prev, [answerId]: false }));
      }
    } else {
      // Flag the answer (comment is optional)
      setSavingFlag((prev) => ({ ...prev, [answerId]: true }));
      try {
        await flagVendorAnswer({
          review_token: reviewToken,
          vendor_risk_assessment_answer_id: answerId,
          is_flagged: true,
          flag_comment: flagComments[answerId]?.trim() || "",
        });

        setFlags((prev) => ({
          ...prev,
          [answerId]: {
            is_flagged: true,
            flag_comment: flagComments[answerId]?.trim() || "",
          },
        }));
        showSnackbar("Answer flagged successfully", "success");
      } catch (error) {
        showSnackbar("Failed to flag answer", "error");
      } finally {
        setSavingFlag((prev) => ({ ...prev, [answerId]: false }));
      }
    }
  };

  const handleFlagCommentBlur = async (answerId) => {
    // Save the flag comment when user leaves the field (only if already flagged)
    if (!flags[answerId]?.is_flagged) return;

    setSavingFlag((prev) => ({ ...prev, [answerId]: true }));
    try {
      await flagVendorAnswer({
        review_token: reviewToken,
        vendor_risk_assessment_answer_id: answerId,
        is_flagged: true,
        flag_comment: flagComments[answerId]?.trim() || "",
      });

      setFlags((prev) => ({
        ...prev,
        [answerId]: {
          ...prev[answerId],
          flag_comment: flagComments[answerId]?.trim() || "",
        },
      }));
    } catch (error) {
      showSnackbar("Failed to save flag comment", "error");
    } finally {
      setSavingFlag((prev) => ({ ...prev, [answerId]: false }));
    }
  };

  const getFlaggedCount = () => {
    return Object.values(flags).filter((f) => f.is_flagged).length;
  };

  const handleSubmitReview = async () => {
    setSubmitting(true);
    try {
      const response = await submitVendorReview({
        review_token: reviewToken,
      });

      if (response.acknowledged) {
        setSuccessMessage(
          "Thank you! You have acknowledged the assessment. The assessment has been submitted successfully."
        );
        setAcknowledged(true);
      } else {
        setSuccessMessage(
          `Your feedback has been sent back for corrections. ${response.flagged_count || 0} answer(s) were flagged. You will receive a new review link once corrections are made.`
        );
      }
      setSubmitted(true);
      setShowSuccess(true);
    } catch (error) {
      showSnackbar(error.message || "Failed to submit review", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleSuccessClose = () => {
    setShowSuccess(false);
    if (onSubmitSuccess) {
      onSubmitSuccess({ acknowledged, submitted: true });
    }
  };

  const getFileNameFromPath = (filePath) => {
    if (!filePath) return "Attachment";
    const parts = filePath.split("/");
    return parts[parts.length - 1] || "Attachment";
  };

  // If already submitted, show thank you / review submitted page
  if (submitted && !showSuccess) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          bgcolor: "#eeeeee",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          p: 3,
        }}
      >
        <Paper sx={{ p: 5, maxWidth: 600, textAlign: "center" }}>
          {acknowledged ? (
            <>
              <CheckCircleIcon sx={{ fontSize: 80, color: "#4caf50", mb: 2 }} />
              <Typography
                variant="h5"
                sx={{ fontFamily: "Lato", fontWeight: "bold", mb: 2, color: PRIMARY }}
              >
                Thank You!
              </Typography>
              <Typography variant="body1" sx={{ fontFamily: "Lato", color: "#666" }}>
                Your review has been submitted and the assessment has been acknowledged successfully.
                You may close this page.
              </Typography>
            </>
          ) : (
            <>
              <CheckCircleIcon sx={{ fontSize: 80, color: "#ff9800", mb: 2 }} />
              <Typography
                variant="h5"
                sx={{ fontFamily: "Lato", fontWeight: "bold", mb: 2, color: PRIMARY }}
              >
                Review Submitted
              </Typography>
              <Typography variant="body1" sx={{ fontFamily: "Lato", color: "#666" }}>
                Your feedback has been sent back for corrections. You will receive a new review link
                once the corrections are made. You may close this page.
              </Typography>
            </>
          )}
        </Paper>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
          bgcolor: "#eeeeee",
        }}
      >
        <CircularProgress size={60} sx={{ color: PRIMARY }} />
      </Box>
    );
  }

  if (!questionnaire) {
    return (
      <Box sx={{ minHeight: "100vh", bgcolor: "#eeeeee", p: 4 }}>
        <Paper sx={{ p: 4, textAlign: "center" }}>
          <Typography variant="h6" sx={{ fontFamily: "Lato", color: "#f44336" }}>
            Questionnaire not found or review link has expired
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%", fontFamily: "Lato" }}>
      {/* Tabs */}
      <Box className="policy-tabs-container">
        <Tabs value={currentTab} onChange={handleTabChange} aria-label="Review Tabs">
          <Tab className="policy-tab" label="Instructions" />
          <Tab className="policy-tab" label="Review Questionnaire" />
        </Tabs>
      </Box>

      {/* Instructions Tab */}
      {currentTab === 0 && (
        <Box sx={{ p: 3 }}>
          <Paper sx={{ p: 4 }}>
            <Typography variant="h6" sx={{ fontFamily: "Lato", fontWeight: "bold", mb: 3 }}>
              Review the vendor risk assessment questionnaire completed on behalf of{" "}
              {vendorName || "your organization"}.
            </Typography>
            <List>
              {REVIEW_INSTRUCTIONS.map((instruction) => (
                <ListItem key={instruction.step} sx={{ alignItems: "flex-start" }}>
                  <ListItemText
                    primary={
                      <Typography sx={{ fontFamily: "Lato", fontWeight: "bold", mb: 1 }}>
                        Step {instruction.step}: {instruction.title}
                      </Typography>
                    }
                    secondary={
                      <Typography sx={{ fontFamily: "Lato", mb: 1 }}>{instruction.text}</Typography>
                    }
                  />
                </ListItem>
              ))}
            </List>
            <Box sx={{ mt: 3, textAlign: "right" }}>
              <ReusableButton
                label="Start Review"
                buttonType="padding"
                onClick={() => setCurrentTab(1)}
              />
            </Box>
          </Paper>
        </Box>
      )}

      {/* Review Questionnaire Tab */}
      {currentTab === 1 && (
        <Box sx={{ p: 3 }}>
          {/* Header */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 3,
            }}
          >
            <Box>
              <Typography variant="h6" sx={{ fontFamily: "Lato", fontWeight: "bold" }}>
                {questionnaire.categories?.[0]?.category_name || "Assessment Review"}
              </Typography>
              {getFlaggedCount() > 0 && (
                <Chip
                  icon={<FlagIcon />}
                  label={`${getFlaggedCount()} Flagged`}
                  color="warning"
                  size="small"
                  sx={{ mt: 1, fontFamily: "Lato" }}
                />
              )}
            </Box>
            <ReusableButton
              label={
                submitting
                  ? "Submitting..."
                  : getFlaggedCount() > 0
                  ? "Submit Review with Flags"
                  : "Acknowledge & Submit"
              }
              buttonType="padding"
              onClick={handleSubmitReview}
              disabled={submitting}
            />
          </Box>

          {/* Sections */}
          {questionnaire.categories?.map((category) =>
            category.subcategories?.map((subcategory) =>
              subcategory.sections?.map((section, sectionIndex) => {
                const sectionId = section.section_id || `section-${sectionIndex}`;
                const flaggedInSection = section.questions?.filter(
                  (q) => flags[q.vendor_risk_assessment_answer_id]?.is_flagged
                ).length;

                return (
                  <Paper
                    key={sectionId}
                    sx={{
                      mb: 2,
                      border: flaggedInSection > 0
                        ? `2px solid #ff9800`
                        : `1px solid #e0e0e0`,
                    }}
                  >
                    <Box
                      sx={{
                        p: 2,
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        cursor: "pointer",
                        bgcolor: flaggedInSection > 0 ? "#fff3e0" : "#f5f5f5",
                        "&:hover": { bgcolor: flaggedInSection > 0 ? "#ffe0b2" : "#eeeeee" },
                      }}
                      onClick={() => handleSectionToggle(sectionId)}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                        {flaggedInSection > 0 && (
                          <FlagIcon sx={{ color: "#f44336", fontSize: 22 }} />
                        )}
                        <Typography variant="h6" sx={{ fontFamily: "Lato", fontWeight: "bold" }}>
                          Section {sectionIndex + 1}: {section.section_name || "Unnamed Section"}
                        </Typography>
                      </Box>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                        {flaggedInSection > 0 && (
                          <Chip
                            icon={<FlagIcon sx={{ fontSize: 16 }} />}
                            label={`${flaggedInSection} Flagged`}
                            size="small"
                            sx={{
                              fontFamily: "Lato",
                              bgcolor: "#f44336",
                              color: "white",
                              fontWeight: "bold",
                              "& .MuiChip-icon": { color: "white" },
                            }}
                          />
                        )}
                        <Typography
                          sx={{ fontFamily: "Lato", color: "#666", fontSize: 14 }}
                        >
                          {section.questions?.length || 0} Questions
                        </Typography>
                        <IconButton>
                          {expandedSections[sectionId] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                      </Box>
                    </Box>

                    <Collapse in={expandedSections[sectionId]}>
                      <Box sx={{ bgcolor: SECTION_HEADER_BG, p: 2 }}>
                        <Typography variant="subtitle2" sx={{ fontFamily: "Lato", fontWeight: "bold" }}>
                          {section.section_name || "Questions"}
                        </Typography>
                      </Box>
                      <Box sx={{ p: 3 }}>
                        {section.questions?.map((question, qIndex) => {
                          const answerId = question.vendor_risk_assessment_answer_id;
                          const isFlagged = flags[answerId]?.is_flagged;
                          const choice = question.vendor_questionnaire_choice;
                          const isSavingFlag = savingFlag[answerId];
                          const wasPreviouslyFlagged = question.was_previously_flagged;

                          return (
                            <Box
                              key={answerId}
                              sx={{
                                mb: 3,
                                p: 2,
                                bgcolor: isFlagged ? FLAG_BG : "white",
                                borderRadius: 1,
                                border: isFlagged
                                  ? `2px solid ${FLAG_COLOR}`
                                  : "1px solid #e0e0e0",
                              }}
                            >
                              {/* Previously Flagged Indicator */}
                              {wasPreviouslyFlagged && !isFlagged && (
                                <Box sx={{ mb: 1 }}>
                                  <Chip
                                    icon={<HistoryIcon sx={{ fontSize: 16 }} />}
                                    label="Previously Flagged"
                                    size="small"
                                    sx={{
                                      fontFamily: "Lato",
                                      bgcolor: "#e3f2fd",
                                      color: "#1565c0",
                                      "& .MuiChip-icon": { color: "#1565c0" },
                                    }}
                                  />
                                </Box>
                              )}

                              {/* Question Header */}
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "flex-start",
                                  justifyContent: "space-between",
                                  gap: 1,
                                }}
                              >
                                <Typography
                                  variant="body1"
                                  sx={{ fontFamily: "Lato", fontWeight: "bold", flex: 1 }}
                                >
                                  {qIndex + 1}. {question.question_description || "Question"}
                                </Typography>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                  {isSavingFlag && <CircularProgress size={16} />}
                                  <Tooltip
                                    title={isFlagged ? "Remove flag" : "Flag this answer"}
                                  >
                                    <IconButton
                                      onClick={() => handleToggleFlag(answerId)}
                                      sx={{
                                        color: isFlagged ? FLAG_COLOR : "#999",
                                        "&:hover": { color: FLAG_COLOR },
                                      }}
                                    >
                                      {isFlagged ? <FlagIcon /> : <FlagOutlinedIcon />}
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              </Box>

                              {/* Answer (left) + Comment (right) side by side */}
                              <Box sx={{ mt: 2, display: "flex", gap: 3, flexWrap: "wrap" }}>
                                {/* Left side: Answer + Attachments */}
                                <Box sx={{ flex: "1 1 45%", minWidth: 0 }}>
                                  <Typography
                                    variant="body2"
                                    sx={{ fontFamily: "Lato", color: "#666", mb: 0.5 }}
                                  >
                                    Answer:
                                  </Typography>
                                  <Chip
                                    label={CHOICE_LABELS[choice] || "Not Answered"}
                                    sx={{
                                      bgcolor: CHOICE_COLORS[choice] || "#e0e0e0",
                                      color: choice ? "white" : "#666",
                                      fontFamily: "Lato",
                                      fontWeight: "bold",
                                    }}
                                  />

                                  {/* Attachments Display */}
                                  {question.attachments && question.attachments.length > 0 && (
                                    <Box sx={{ mt: 2 }}>
                                      <Typography
                                        variant="body2"
                                        sx={{ fontFamily: "Lato", fontWeight: "bold", mb: 1 }}
                                      >
                                        Evidence / Attachments:
                                      </Typography>
                                      {question.attachments.map((attachment, idx) => {
                                        const fileName =
                                          typeof attachment === "string"
                                            ? getFileNameFromPath(attachment)
                                            : attachment.file_name || "Attachment";
                                        const presignedUrl =
                                          typeof attachment === "object"
                                            ? attachment.presigned_url
                                            : null;

                                        return (
                                          <Box
                                            key={idx}
                                            sx={{
                                              display: "flex",
                                              alignItems: "center",
                                              gap: 1,
                                              mb: 0.5,
                                            }}
                                          >
                                            <AttachFileIcon fontSize="small" sx={{ color: "#666" }} />
                                            {presignedUrl ? (
                                              <Typography
                                                variant="body2"
                                                component="a"
                                                href={presignedUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                sx={{
                                                  fontFamily: "Lato",
                                                  color: "#1976d2",
                                                  textDecoration: "underline",
                                                  cursor: "pointer",
                                                  display: "flex",
                                                  alignItems: "center",
                                                  gap: 0.5,
                                                }}
                                              >
                                                {fileName}
                                                <OpenInNewIcon sx={{ fontSize: 14 }} />
                                              </Typography>
                                            ) : (
                                              <Typography variant="body2" sx={{ fontFamily: "Lato" }}>
                                                {fileName}
                                              </Typography>
                                            )}
                                          </Box>
                                        );
                                      })}
                                    </Box>
                                  )}
                                </Box>

                                {/* Right side: Comment + Flag Comment */}
                                <Box sx={{ flex: "1 1 45%", minWidth: 0 }}>
                                  <Typography
                                    variant="body2"
                                    sx={{ fontFamily: "Lato", color: "#666", mb: 0.5 }}
                                  >
                                    Comment:
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      fontFamily: "Lato",
                                      p: 1,
                                      borderRadius: 1,
                                      minHeight: 40,
                                      border: "1px solid #e0e0e0",
                                    }}
                                  >
                                    {question.comment || "No comment provided"}
                                  </Typography>

                                  {/* Comment History */}
                                  {question.comment_history && question.comment_history.length > 0 && (
                                    <Box
                                      sx={{
                                        mt: 1.5,
                                        p: 1.5,
                                        bgcolor: "#f5f5f5",
                                        borderRadius: 1,
                                        border: "1px solid #e0e0e0",
                                      }}
                                    >
                                      <Typography
                                        variant="body2"
                                        sx={{ fontFamily: "Lato", fontWeight: "bold", mb: 1, color: "#333" }}
                                      >
                                        Comment History:
                                      </Typography>
                                      {question.comment_history.map((h, idx) => (
                                        <Box
                                          key={idx}
                                          sx={{
                                            mb: 0.5,
                                            pl: 1,
                                            borderLeft: h.comment_type === "review_comment"
                                              ? "3px solid #f44336"
                                              : "3px solid #1976d2",
                                          }}
                                        >
                                          <Typography
                                            variant="caption"
                                            sx={{
                                              fontFamily: "Lato",
                                              color: h.comment_type === "review_comment" ? "#d32f2f" : "#1565c0",
                                              fontWeight: "bold",
                                            }}
                                          >
                                            {h.comment_type === "review_comment" ? "Review" : "Answer"} — Round {h.round_number}
                                          </Typography>
                                          <Typography variant="body2" sx={{ fontFamily: "Lato", color: "#555" }}>
                                            {h.comment_text}
                                          </Typography>
                                        </Box>
                                      ))}
                                    </Box>
                                  )}

                                  {/* Inline Flag Comment Box */}
                                  {isFlagged && (
                                    <Box sx={{ mt: 2 }}>
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          fontFamily: "Lato",
                                          fontWeight: "bold",
                                          color: FLAG_COLOR,
                                          mb: 0.5,
                                        }}
                                      >
                                        Review Comment (Optional):
                                      </Typography>
                                      <TextField
                                        fullWidth
                                        multiline
                                        minRows={2}
                                        maxRows={4}
                                        placeholder="Describe what is incorrect or needs to be changed..."
                                        value={flagComments[answerId] || ""}
                                        onChange={(e) =>
                                          setFlagComments((prev) => ({
                                            ...prev,
                                            [answerId]: e.target.value,
                                          }))
                                        }
                                        onBlur={() => handleFlagCommentBlur(answerId)}
                                        sx={{
                                          "& .MuiOutlinedInput-root": {
                                            fontFamily: "Lato",
                                            overflowX: "hidden",
                                            wordBreak: "break-word",
                                          },
                                        }}
                                      />
                                    </Box>
                                  )}
                                </Box>
                              </Box>
                            </Box>
                          );
                        })}
                      </Box>
                    </Collapse>
                  </Paper>
                );
              })
            )
          )}
        </Box>
      )}

      {/* Success Modal */}
      <SuccessModal
        open={showSuccess}
        onClose={handleSuccessClose}
        title="Review Submitted!"
        message={successMessage}
        icon={<CheckCircleIcon sx={{ fontSize: 60, color: "#4caf50" }} />}
      />
    </Box>
  );
};

export default VendorReviewQuestionnaire;
